import PhoneInputWithCountry from "react-phone-number-input";
import {
	getExampleNumber,
	parsePhoneNumberFromString,
} from "libphonenumber-js";
import mobileExamples from "libphonenumber-js/examples.mobile.json";
import "react-phone-number-input/style.css";

// Common timezone → ISO-3166 country code (covers the regions visitors most likely
// come from). Falls back to navigator.language, then UA.
const TZ_TO_COUNTRY = {
	// Ukraine
	"Europe/Kiev": "UA", "Europe/Kyiv": "UA", "Europe/Simferopol": "UA",
	"Europe/Uzhgorod": "UA", "Europe/Zaporozhye": "UA",
	// Europe
	"Europe/London": "GB", "Europe/Belfast": "GB", "Europe/Guernsey": "GB", "Europe/Jersey": "GB",
	"Europe/Dublin": "IE",
	"Europe/Berlin": "DE", "Europe/Busingen": "DE",
	"Europe/Paris": "FR",
	"Europe/Madrid": "ES", "Europe/Ceuta": "ES",
	"Europe/Rome": "IT", "Europe/Vatican": "VA",
	"Europe/Lisbon": "PT", "Atlantic/Azores": "PT", "Atlantic/Madeira": "PT",
	"Europe/Amsterdam": "NL", "Europe/Brussels": "BE", "Europe/Luxembourg": "LU",
	"Europe/Vienna": "AT", "Europe/Zurich": "CH",
	"Europe/Warsaw": "PL", "Europe/Prague": "CZ", "Europe/Bratislava": "SK",
	"Europe/Budapest": "HU", "Europe/Bucharest": "RO", "Europe/Sofia": "BG",
	"Europe/Athens": "GR", "Europe/Istanbul": "TR",
	"Europe/Helsinki": "FI", "Europe/Mariehamn": "AX",
	"Europe/Stockholm": "SE", "Europe/Oslo": "NO", "Europe/Copenhagen": "DK", "Europe/Reykjavik": "IS",
	"Europe/Vilnius": "LT", "Europe/Riga": "LV", "Europe/Tallinn": "EE",
	"Europe/Minsk": "BY", "Europe/Chisinau": "MD",
	"Europe/Moscow": "RU", "Europe/Kaliningrad": "RU", "Europe/Volgograd": "RU",
	"Europe/Samara": "RU", "Europe/Astrakhan": "RU", "Europe/Saratov": "RU",
	"Europe/Belgrade": "RS", "Europe/Zagreb": "HR", "Europe/Sarajevo": "BA",
	"Europe/Ljubljana": "SI", "Europe/Skopje": "MK", "Europe/Podgorica": "ME",
	"Europe/Tirane": "AL", "Europe/Andorra": "AD", "Europe/Malta": "MT",
	"Europe/Monaco": "MC", "Europe/Gibraltar": "GI",
	// Americas (sample)
	"America/New_York": "US", "America/Detroit": "US", "America/Chicago": "US",
	"America/Denver": "US", "America/Phoenix": "US", "America/Los_Angeles": "US",
	"America/Anchorage": "US", "Pacific/Honolulu": "US",
	"America/Toronto": "CA", "America/Vancouver": "CA", "America/Montreal": "CA",
	"America/Edmonton": "CA", "America/Winnipeg": "CA", "America/Halifax": "CA",
	"America/Mexico_City": "MX", "America/Sao_Paulo": "BR", "America/Buenos_Aires": "AR",
	// Asia
	"Asia/Tokyo": "JP", "Asia/Shanghai": "CN", "Asia/Hong_Kong": "HK",
	"Asia/Seoul": "KR", "Asia/Singapore": "SG", "Asia/Bangkok": "TH",
	"Asia/Jakarta": "ID", "Asia/Manila": "PH", "Asia/Kuala_Lumpur": "MY",
	"Asia/Dubai": "AE", "Asia/Riyadh": "SA", "Asia/Tehran": "IR",
	"Asia/Jerusalem": "IL", "Asia/Tel_Aviv": "IL",
	"Asia/Tbilisi": "GE", "Asia/Yerevan": "AM", "Asia/Baku": "AZ",
	"Asia/Almaty": "KZ", "Asia/Tashkent": "UZ",
	// Oceania / Africa (sample)
	"Australia/Sydney": "AU", "Australia/Melbourne": "AU", "Australia/Perth": "AU",
	"Pacific/Auckland": "NZ",
	"Africa/Cairo": "EG", "Africa/Lagos": "NG", "Africa/Johannesburg": "ZA",
};

function guessDefaultCountry() {
	try {
		const tz = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone;
		if (tz && TZ_TO_COUNTRY[tz]) return TZ_TO_COUNTRY[tz];
	} catch {
		/* noop */
	}
	try {
		const lang = (typeof navigator !== "undefined" && navigator.language) || "uk-UA";
		const region = lang.split("-")[1]?.toUpperCase();
		if (region && /^[A-Z]{2}$/.test(region)) return region;
	} catch {
		/* noop */
	}
	return "UA";
}

const DEFAULT_COUNTRY = guessDefaultCountry();

// Cache so we don't recompute the example for every keystroke.
const MAX_TOTAL_DIGITS_CACHE = new Map();
const E164_MAX = 15; // E.164 hard ceiling

function getMaxTotalDigits(country) {
	if (!country) return E164_MAX;
	if (MAX_TOTAL_DIGITS_CACHE.has(country)) return MAX_TOTAL_DIGITS_CACHE.get(country);
	const ex = getExampleNumber(country, mobileExamples);
	const max = ex
		? String(ex.countryCallingCode).length + ex.nationalNumber.length
		: E164_MAX;
	MAX_TOTAL_DIGITS_CACHE.set(country, max);
	return max;
}

// Best-guess country: parse what's typed, fall back to default.
function detectCountry(value) {
	if (!value) return DEFAULT_COUNTRY;
	try {
		return parsePhoneNumberFromString(value)?.country ?? DEFAULT_COUNTRY;
	} catch {
		return DEFAULT_COUNTRY;
	}
}

export default function PhoneInput({
	value,
	onChange,
	className = "",
	placeholder = "+38 (0__) ___-__-__",
	required = true,
	id,
	name = "phone",
	"aria-invalid": ariaInvalid,
}) {
	// When the new value exceeds the country's mobile-example length, trim to that.
	const handleChange = (next) => {
		if (!next) {
			onChange?.(next);
			return;
		}
		const country = detectCountry(next);
		const max = getMaxTotalDigits(country);
		const digits = (next.match(/\d/g) || []).length;
		if (digits > max) {
			let trimmed = next;
			while (trimmed && (trimmed.match(/\d/g) || []).length > max) {
				trimmed = trimmed.slice(0, -1);
			}
			onChange?.(trimmed || undefined);
			return;
		}
		onChange?.(next);
	};

	// Keystroke-level block: predict the result and reject the digit if it would overflow.
	const handleKeyDown = (e) => {
		if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return;
		if (!/\d/.test(e.key)) return;
		const t = e.target;
		if (t.selectionStart !== t.selectionEnd) return;
		const predicted = t.value.slice(0, t.selectionStart) + e.key + t.value.slice(t.selectionEnd);
		const digits = predicted.replace(/\D/g, "");
		if (!digits) return;
		const country = detectCountry("+" + digits) || detectCountry(value);
		const max = getMaxTotalDigits(country);
		if (digits.length > max) e.preventDefault();
	};

	// Reject paste content that would overflow.
	const handlePaste = (e) => {
		const pasted = e.clipboardData?.getData?.("text") ?? "";
		if (!pasted) return;
		const t = e.target;
		const predicted = t.value.slice(0, t.selectionStart) + pasted + t.value.slice(t.selectionEnd);
		const digits = predicted.replace(/\D/g, "");
		if (!digits) return;
		const country = detectCountry("+" + digits) || detectCountry(value);
		const max = getMaxTotalDigits(country);
		if (digits.length > max) e.preventDefault();
	};

	return (
		<PhoneInputWithCountry
			id={id}
			name={name}
			international
			defaultCountry={DEFAULT_COUNTRY}
			value={value}
			onChange={handleChange}
			placeholder={placeholder}
			className={`phone-input ${className}`.trim()}
			numberInputProps={{
				required,
				"aria-invalid": ariaInvalid,
				maxLength: 22,
				inputMode: "tel",
				autoComplete: "tel",
				onKeyDown: handleKeyDown,
				onPaste: handlePaste,
			}}
		/>
	);
}

